// Admin grants data layer (epic #1259) — the UI half of `/v1/admin/grants` and
// the MCP `grant` verb, and the FIRST write plane the platform-admin console
// owned over REST (the `platform` overrides followed: `lib/admin-overrides.ts`).
//
// A grant is free capacity the platform hands an org or a person: a FLAG kind
// that lifts a ceiling outright, or a QUANTITY kind carrying how much. Which is
// which is `grantKindIsFlag`, and the vocabulary is `GrantKindSchema` — not a
// list restated here, because the list restated in four places is exactly how
// `unlimited_spend` came to be legal in the enum, refused by the database, and
// demanded an amount by the form. It
// replaced a hand-written psql session against prod, so every one carries a
// SUBJECT, an ISSUER, a REASON and a lifetime — and that is the whole reason a
// screen exists: the audit story is only useful if a human can read it back.
//
// TWO SCOPES, ONE PAGE. The list rides `admin:read:orgs` (the staff radar's own
// read, so a staff SSO session sees who holds free capacity); issue and revoke
// ride `admin:grants`, which no session carries. They take a one-hour operator
// credential (`withOperatorCredential` in `./grant-actions.ts`), and this file
// holds the door that issues one: `issueOperatorToken` is the ONE call to
// `POST /v1/admin/operator-tokens` in the SPA (#5709, #5724). Proof: it is the
// single entry of `SESSION_WRITE_EXEMPT` in `scripts/admin-session-writes.test.ts`,
// so any other caller asking the door on the session is red.
//
// THE SUBJECT IS PICKED, NEVER TYPED, and that is a correctness rule rather than
// a convenience. `GET /v1/admin/grants` does NOT resolve the subject — it reads
// `grants` by `(subject_kind, subject_id)` and answers `{data: []}` for an id
// nobody holds, indistinguishable from a subject with no grants. So a pasted
// typo reads as "this org has no grants" and an operator issues a second one.
// `useGrantSubjectSearch` takes the id from the registry's own answer instead,
// which is the one shape that cannot be mistyped. (The ISSUE path does resolve
// it — `unknown_subject`, 422 — but by then the wrong read has already been
// believed.)
//
// IT PAGES, and the cursor is the route's own. `GET /v1/admin/grants` answers
// `{data, next_cursor}` off an opaque OFFSET cursor, so this is a page reader fed
// to `usePagedQuery` and the panel carries a Load-more. It used to ask for the
// route's ceiling and label a full page "these are the first 100" — which was the
// honest thing to say about a route that minted no token, and is a lie now that
// one exists: page two is reachable, so it is reached.
//
// `per_page` is the parameter. The `?limit` alias is gone — the route 400s it by
// name — so `per_page` is the only spelling that pages.

import {
  GRANT_AMOUNT_MAX,
  GRANT_REASON_MAX_CHARS,
  GRANT_SUBJECT_KINDS,
  type GrantKind,
  type GrantSubjectKind,
  GrantSubjectKindSchema,
  type GrantWire,
} from '@developerz/domain';
import { createQuery } from '@tanstack/solid-query';
import { z } from 'zod';
import type { AdminAccountRow, AdminUserRow } from './admin';
import { apiFetch } from './api';
import type { OperatorScope, OperatorSend } from './operator-credential';
import type { Page } from './paged';
import { SESSION_TOKEN } from './session';

export type { GrantKind, GrantSubjectKind };
export { GRANT_AMOUNT_MAX, GRANT_REASON_MAX_CHARS, GRANT_SUBJECT_KINDS };

/** The two subject axes, in the domain's own order — the picker's vocabulary. */
export const GRANT_SUBJECT_KIND_OPTIONS: readonly GrantSubjectKind[] =
  GrantSubjectKindSchema.options;

/**
 * One grant as `/v1/admin/grants` serves it — INFERRED from `GrantResource`
 * (`packages/domain/src/wire/grant.ts`), the one declaration this route and the
 * `grant` verb both serialize through, so the two doors can no longer describe
 * a grant differently.
 *
 * `active` is the SERVER's answer, resolved for every row of a listing at one
 * frozen instant. Nothing here re-derives it from `expires_iso` and a browser
 * clock: the half-open expiry window is a domain rule (`grantActive`), and a
 * second copy running on a skewed clock would disagree with the gate that
 * actually decides whether the capacity is live.
 *
 * `subject_kind` and `kind` are restated NARROWER, at the domain's own enums.
 * The wire schema types them `z.string()` because `GrantEntity` — the
 * structural minimum both doors satisfy — does, and its own comment says
 * narrowing it is a port change across `deps/grants.ts` rather than a wire
 * extraction. Until that lands, this screen keeps the enums: they are the
 * picker's vocabulary and the label catalogue's key set, and widening them to
 * `string` would make every unhandled kind render as a missing label instead of
 * failing to compile.
 */
export type AdminGrant = Readonly<Omit<GrantWire, 'subject_kind' | 'kind'>> & {
  readonly subject_kind: GrantSubjectKind;
  readonly kind: GrantKind;
};

/**
 * A grant's state label and tone — read off the server's own fields, never a
 * clock: `active` is the server's answer, `revoked_iso` splits "a human pulled
 * it" from "its expiry passed".
 */
export function grantState(grant: AdminGrant): {
  readonly key: string;
  // Mirrors StatusPill's `StatusTone`, restated: `scripts/tsconfig.json` (no `jsx`)
  // compiles this file via the mocks, and importing a `.tsx` there is TS6142.
  readonly tone: 'success' | 'danger' | 'warning';
} {
  if (grant.active) return { key: 'admin.grants_state_active', tone: 'success' };
  if (grant.revoked_iso !== null) return { key: 'admin.grants_state_revoked', tone: 'danger' };
  return { key: 'admin.grants_state_expired', tone: 'warning' };
}

/** A revoke's answer: the parked row plus whether THIS call is what stopped it. */
export type AdminGrantRevoked = AdminGrant & { readonly revoked: boolean };

/**
 * WHOM the page is addressing. Both halves are required — the route 400s
 * without either, since the two axes share one column and defaulting one would
 * answer about a different subject than the operator asked about. `label` is
 * the registry's own name for it, carried for the heading and never sent.
 */
export type GrantSubject = {
  readonly kind: GrantSubjectKind;
  readonly id: string;
  readonly label: string;
};

/**
 * The subject a `/admin/grants?subject_kind=…&subject_id=…&subject_label=…` URL
 * names, or null when it names none or names it badly (an unknown axis, a blank
 * id). The label falls back to the id so the headings always say something; a
 * repeated param takes its first value, the router's own `string | string[]`.
 *
 * A pasted URL is the one place a subject can arrive TYPED rather than picked
 * (the rule in the header), and the mitigation is the same as the picker's: the
 * link that mints these carries the registry's own id (`<AdminUserActions/>`), and
 * a wrong id reads as "no grants" exactly as the header warns — so this parses
 * and never guesses.
 */
export function grantSubjectFromParams(params: {
  readonly subject_kind?: string | string[];
  readonly subject_id?: string | string[];
  readonly subject_label?: string | string[];
}): GrantSubject | null {
  const first = (v: string | string[] | undefined): string => (Array.isArray(v) ? v[0] : v) ?? '';
  const kind = GrantSubjectKindSchema.safeParse(first(params.subject_kind));
  const id = first(params.subject_id).trim();
  if (!kind.success || id === '') return null;
  const label = first(params.subject_label).trim();
  return { kind: kind.data, id, label: label === '' ? id : label };
}

/** The `/admin/grants` URL addressing `subject` — the inverse of `grantSubjectFromParams`. */
export function grantSubjectHref(subject: GrantSubject): string {
  const params = new URLSearchParams({
    subject_kind: subject.kind,
    subject_id: subject.id,
    subject_label: subject.label,
  });
  return `/admin/grants?${params.toString()}`;
}

/** One search hit, projected from whichever registry the kind names. */
export type GrantSubjectOption = {
  readonly id: string;
  readonly label: string;
  /** Second line — an account's kind + tier, a person's email. May be empty. */
  readonly hint: string;
};

export const ADMIN_GRANTS_KEY = 'admin_grants';
export const ADMIN_GRANT_SUBJECTS_KEY = 'admin_grant_subjects';

/** Rows per page. Well inside the route's ceiling (`LIST_MAX_LIMIT`, 100). */
export const GRANT_PAGE_SIZE = 30;

/** A short pick list, not a registry page — the operator narrows with the query. */
const SUBJECT_SEARCH_PAGE_SIZE = 10;

/**
 * ONE page of a subject's grant HISTORY, newest first — revoked and expired rows
 * included, each carrying its resolved `active`. Idle until a subject is picked,
 * so the page issues no read it cannot address.
 *
 * Hand it to `usePagedQuery`: the cursor accessor is part of the query key, so
 * the walk refetches itself. The subject is in that key too, which is what makes
 * a re-pick a new read rather than page two of somebody else's history.
 */
export function useAdminGrants(
  subject: () => GrantSubject | null,
  cursor: () => string | null,
  token: string | null = SESSION_TOKEN,
) {
  return createQuery<Page<AdminGrant>>(() => ({
    queryKey: [ADMIN_GRANTS_KEY, token, subject()?.kind ?? '', subject()?.id ?? '', cursor()],
    enabled: token !== null && subject() !== null,
    queryFn: () => {
      const at = subject() as GrantSubject;
      const params = new URLSearchParams({
        subject_kind: at.kind,
        subject_id: at.id,
        per_page: String(GRANT_PAGE_SIZE),
      });
      const from = cursor();
      if (from !== null) params.set('cursor', from);
      return apiFetch<Page<AdminGrant>>(`/v1/admin/grants?${params.toString()}`, token);
    },
  }));
}

/**
 * Resolve a typed name to a real subject id, against the registry the KIND
 * names — `/v1/admin/accounts` or `/v1/admin/users`, both already read by this
 * console on the same `admin:read:orgs` / `admin:read:users` staff scopes.
 *
 * Deliberately NOT `useAdminAccounts`/`useAdminUsers`: those are the
 * registries' own cursor-paginated reads, always enabled, in two row shapes.
 * This is a one-shot lookup that stays IDLE until a query is typed (an admin
 * page must not fetch two registries nobody asked for) and projects both shapes
 * to one option, so the picker holds no per-kind branch of its own.
 */
export function useGrantSubjectSearch(
  kind: () => GrantSubjectKind,
  q: () => string,
  token: string | null = SESSION_TOKEN,
) {
  return createQuery<readonly GrantSubjectOption[]>(() => ({
    queryKey: [ADMIN_GRANT_SUBJECTS_KEY, token, kind(), q().trim()],
    enabled: token !== null && q().trim() !== '',
    queryFn: async () => {
      const params = new URLSearchParams({
        q: q().trim(),
        per_page: String(SUBJECT_SEARCH_PAGE_SIZE),
      });
      if (kind() === 'account') {
        const page = await apiFetch<Page<AdminAccountRow>>(
          `/v1/admin/accounts?${params.toString()}`,
          token,
        );
        return page.data.map((row) => ({
          id: row.id,
          label: row.github_login,
          hint: `${row.kind} · ${row.tier}`,
        }));
      }
      const page = await apiFetch<Page<AdminUserRow>>(
        `/v1/admin/users?${params.toString()}`,
        token,
      );
      return page.data.map((row) => ({ id: row.id, label: row.login, hint: row.email ?? '' }));
    },
  }));
}

/**
 * What an operator asks for. `source` is stamped `operator` server-side and the
 * issuer comes from the authenticated session, so neither is a field here — an
 * operator must not be able to file their giveaway under a colleague's name.
 * The body is `.strict()`, so an omitted `amount` / `expires_at` is the ONLY
 * way to say "none": sending a null would be a 400.
 */
export type IssueGrantInput = {
  readonly subject_kind: GrantSubjectKind;
  readonly subject_id: string;
  readonly kind: GrantKind;
  readonly amount?: number;
  readonly reason: string;
  readonly expires_at?: string;
};

/**
 * Issue one, sent on an `admin:grants` operator credential — `send` comes from
 * `withOperatorCredential` (`./grant-actions.ts`). 201 with the recorded grant;
 * every refusal is a 422 the caller renders.
 */
export function issueGrant(input: IssueGrantInput, send: OperatorSend): Promise<AdminGrant> {
  return send<AdminGrant>('/v1/admin/grants', { method: 'POST', body: JSON.stringify(input) });
}

/**
 * Stop one, sent on an `admin:grants` operator credential. PARKS the row — nothing
 * is deleted, so the grant and its reason stay legible after the capacity is
 * gone. `revoked: false` means it was ALREADY stopped (a re-run, or a second
 * operator racing the first): a benign no-op the caller reports, not an error.
 */
export function revokeGrant(id: string, send: OperatorSend): Promise<AdminGrantRevoked> {
  return send<AdminGrantRevoked>(`/v1/admin/grants/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });
}

/**
 * `POST /v1/admin/operator-tokens`' answer, checked where it enters. `token` is
 * the plaintext, shown exactly once, and the write's bearer; `expires_iso` is
 * when `withOperatorCredential` stops holding it. Unchecked, a 2xx without them
 * sent the write as `Bearer undefined` and held it on a NaN clock; now it throws
 * `ApiSchemaError` and no write is sent. Only the two fields this client reads
 * are named, and the object is non-strict (`parseBody`'s rule), so the route's
 * other fields (`id`, `scopes`, `name`) or a new one never break a loaded bundle.
 */
const OperatorTokenIssuedSchema = z.object({
  token: z.string().min(1),
  expires_iso: z.iso.datetime(),
});
export type OperatorTokenIssued = z.infer<typeof OperatorTokenIssuedSchema>;

/**
 * Ask the operator-credential door for a one-hour credential carrying exactly
 * `scopes`, asked with the SESSION (the door refuses a PAT). The raw call: hold
 * and reuse go through `withOperatorCredential`, never around it. Refusals throw
 * as `ApiError` (403 `forbidden`, or 403 `reauth-required`) for that seam to tell
 * apart.
 */
export function issueOperatorToken(
  scopes: readonly OperatorScope[],
  name: string,
  token: string | null = SESSION_TOKEN,
): Promise<OperatorTokenIssued> {
  return apiFetch(
    '/v1/admin/operator-tokens',
    token,
    { method: 'POST', body: JSON.stringify({ scopes, name }) },
    { schema: OperatorTokenIssuedSchema },
  );
}

/**
 * A `<input type="datetime-local">` value → the ISO instant the body carries, or
 * null for blank/unparseable. The browser hands back a wall-clock string with no
 * zone, which `Date` reads as LOCAL time — which is what the operator typing
 * "5pm" means — so the conversion to UTC happens here, once, rather than in a
 * form that would have to remember it.
 */
export function expiryToIso(local: string): string | null {
  if (local.trim() === '') return null;
  const at = new Date(local);
  return Number.isNaN(at.getTime()) ? null : at.toISOString();
}
